















































import Vue from 'vue';
import MStoreSimpleTable from '@/components/shared/Table/MStoreSimpleTable.vue';
import { ComponentHelper, ModalHelpers, RouteHelpers } from '@/mixins';
import { ColourVariantType } from '@/types';
import ApiConfiguration from '@/classes/ApiConfiguration';
import { ExternalUserModel, ApiKeyResponseModel } from '@/services/GeneratedApi';
import Toast from '@/classes/SnotifyConfiguration';

export default Vue.extend({
  name: 'ExternalAPIKeyManagement',
  mixins: [ComponentHelper, RouteHelpers],
  components: {
    MStoreSimpleTable,
    LoadingSpinner: () => import('@/components/shared/LoadingSpinner.vue'),
    LoadingOverlay: () => import('@/components/shared/LoadingOverlay.vue')
  },
  data() {
    return {
      searchText: '',
      isRefreshing: false,
      isLoading: false,
      isFetchingUsers: false,
      isAssigningRevokingApiKey: false,
      loadingMessage: '',
      users: [] as ExternalUserModel[],
      currentPage: 1,
      itemsPerPage: 20,
      fields: [
        { key: 'userId', description: 'ID' },
        { key: 'username', description: 'Username' },
        { key: 'name', description: 'Name' },
        { key: 'emailAddress', description: 'Email' },
        { key: 'organisation', description: 'Organisation' }
      ]
    };
  },
  computed: {
    filteredResults(this: any): ExternalUserModel[] {
      if (!this.searchText) {
        return this.users;
      }

      const search = this.searchText.toLowerCase();
      return this.users.filter(
        (user: ExternalUserModel) =>
          user.userId?.toString().includes(search) ||
          user.username?.toLowerCase().includes(search) ||
          user.name?.toLowerCase().includes(search) ||
          user.emailAddress?.toLowerCase().includes(search) ||
          user.organisation?.toLowerCase().includes(search)
      );
    },
    paginatedResults(this: any): ExternalUserModel[] {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredResults.slice(start, end);
    }
  },
  methods: {
    async assignKey(this: any, user: ExternalUserModel) {
      try {
        this.isAssigningRevokingApiKey = true;
        this.loadingMessage = 'Assigning API key...';
        const response: ApiKeyResponseModel = await ApiConfiguration.api().externalUsers.externalUsersApiKeyCreate(user.userId!);
        this.isAssigningRevokingApiKey = false;

        const userIndex = this.users.findIndex((u: ExternalUserModel) => u.userId === user.userId);
        if (userIndex !== -1) {
          this.users[userIndex].hasApiKey = true;
          this.users[userIndex].apiKeyRevoked = false;
        }
        try {
          await ModalHelpers.methods.showApiKeyModalAsync.call(this, this.$bvModal, response.apiKey || '', 'API Key Assignment Successful');
        } catch (modalError) {
          console.error('Modal error:', modalError);
        }
      } catch (error) {
        console.error('API error:', error);
        Toast.showError('Failed to assign API key. Please try again.');
      } finally {
        this.isAssigningRevokingApiKey = false;
        this.loadingMessage = '';
      }
    },
    async revokeKey(this: any, user: ExternalUserModel) {
      const confirmed = await ModalHelpers.methods.showConfirmationModalAsync(this.$bvModal, 'Are you sure you want to revoke the API Key for this user?', {
        okVariant: ColourVariantType.Danger,
        okTitle: 'Revoke',
        cancelTitle: 'Cancel',
        title: 'API Key Revocation'
      });

      if (confirmed) {
        try {
          this.isAssigningRevokingApiKey = true;
          this.loadingMessage = 'Revoking API key...';
          await ApiConfiguration.api().externalUsers.externalUsersApiKeyRevokeCreate(user.userId!);
          this.isAssigningRevokingApiKey = false;

          const userIndex = this.users.findIndex((u: ExternalUserModel) => u.userId === user.userId);
          if (userIndex !== -1) {
            this.users[userIndex].hasApiKey = false;
            this.users[userIndex].apiKeyRevoked = true;
          }

          Toast.showSuccess('API key has been successfully revoked.');
        } catch (error) {
          Toast.showError('Failed to revoke API key. Please try again.');
        } finally {
          this.isAssigningRevokingApiKey = false;
          this.loadingMessage = '';
        }
      }
    },
    onRefreshButtonClicked(this: any) {
      this.clearFilterValue();
      this.fetchUsers();
    },
    updateFilterValue(this: any, value: string) {
      this.searchText = value;
      this.currentPage = 1;
    },
    clearFilterValue(this: any) {
      this.searchText = '';
      this.currentPage = 1;
    },
    async fetchUsers(this: any) {
      this.isFetchingUsers = true;
      try {
        const users = (await ApiConfiguration.apiWithErrorPage().externalUsers.externalUsersUsersList()) as ExternalUserModel[];
        this.users = users;
      } catch (error) {
        Toast.showError('Failed to load users.');
      } finally {
        this.isFetchingUsers = false;
      }
    }
  },
  async mounted() {
    this.isLoading = true;
    await this.fetchUsers();
    this.isLoading = false;
  }
});
